import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { TabPanel, Tabs } from '@/ui/Tabs';
import { Asignaciones } from './Asignaciones';
import { EditorDeQuiz } from './EditorDeQuiz';
import { Lecciones } from './Lecciones';
import { Portada } from './Portada';
import type { CursoDetalle } from '../api';

/**
 * GestionDeCurso — el panel de quien gestiona un curso: lecciones, quiz,
 * portada y asignaciones.
 *
 * ## Por qué sus confirmaciones ya no cierran el panel
 *
 * El panel vanilla era un `Utils.modal`, y sus cuatro confirmaciones —borrar una
 * lección, borrar una pregunta, borrar una asignación y reiniciar el progreso de
 * alguien— eran `Utils.confirm`, que **pinta en el mismo `#modal-overlay`**. La
 * pregunta sustituía al panel del que salía y, al aceptar, lo cerraba.
 *
 * Y lo que venía después era peor. Tras borrar, el código repintaba el panel
 * con `document.getElementById('manage-panel').innerHTML = …`, y ese nodo ya no
 * existía:
 *
 *     borrar una lección      →  «Lección eliminada» y, justo detrás,
 *     borrar una pregunta          «Cannot set properties of null»
 *     borrar una asignación   →  el panel se cierra sin decir nada
 *     reiniciar un progreso   →  ídem, con una promesa rechazada en la consola
 *
 * Leído del código, no reproducido en el navegador: el camino es determinista
 * —`Utils.confirm` llama a `modal()` y luego a `closeModal()`— y `CLAUDE.md` ya
 * avisaba de esta trampa para cualquier pregunta hecha por encima de un modal.
 *
 * Aquí el panel es un `Drawer` y cada confirmación un `ConfirmDialog` propio,
 * los dos con su portal: la pregunta se abre **encima** del panel y el panel
 * sigue ahí cuando se cierra.
 *
 * ## Las pestañas se montan al abrirlas
 *
 * `TabPanel` no usa `forceMount`, así que abrir el panel pide las lecciones —que
 * ya vienen con el curso— y nada más. El quiz guardado, las asignaciones y las
 * áreas se piden al entrar en su pestaña.
 */
export function GestionDeCurso({
  curso, abierto, onCerrar,
}: {
  curso: CursoDetalle;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const [pestana, setPestana] = useState('lecciones');

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Gestionar el curso"
      descripcion={curso.title}
      ancho="lg"
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      <Tabs
        valor={pestana}
        onCambio={setPestana}
        pestanas={[
          { clave: 'lecciones', etiqueta: 'Lecciones', icono: 'bi-collection-play' },
          { clave: 'quiz', etiqueta: 'Quiz', icono: 'bi-patch-question' },
          { clave: 'portada', etiqueta: 'Portada', icono: 'bi-image' },
          { clave: 'asignar', etiqueta: 'Asignar', icono: 'bi-person-plus' },
        ]}
      >
        <TabPanel clave="lecciones"><Lecciones curso={curso} /></TabPanel>
        <TabPanel clave="quiz"><EditorDeQuiz curso={curso} /></TabPanel>
        <TabPanel clave="portada"><Portada curso={curso} /></TabPanel>
        <TabPanel clave="asignar"><Asignaciones curso={curso} /></TabPanel>
      </Tabs>
    </Drawer>
  );
}
